import React from 'react';
import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import Login from '../pages/Login/Login';
import Dashboard from '../pages/Dashboard/Dashboard';
import StudentList from '../pages/Students/StudentList/StudentList';
import Payments from '../pages/Payments/Payments';
import Orchestrator from '../pages/Orchestrator/Orchestrator';
import OrchestratorFup from '../pages/Orchestrator/OrchestratorFup';
import Performance from '../pages/Performance/Performance';
import WorkoutDeliveryManagementPage from '../pages/WorkoutDeliveryManagement/WorkoutDeliveryManagement';
import StudentHistoryPage from '../pages/StudentHistory/StudentHistoryPage';
import Logs from '../pages/Logs/Logs';
import PaymentErrors from '../pages/PaymentErrors/PaymentErrors';
import SysHome from '../pages/Sys/SysHome';
import FixSubscriptions from '../pages/FixSubscriptions/FixSubscriptions';
import StudentRegister from '../pages/Students/StudentRegister/StudentRegister';
import StudentTraining from '../pages/Students/StudentTraining/StudentTraining';
import NotFound from '../pages/NotFound/NotFound';
import LeadForm from '../pages/Commercial/LeadForm';
import AnamnesisRegister from '../pages/Anamnesis/AnamnesisRegister/AnamnesisRegister';
import Landpage from '../pages/Landpage/Landpage';
import ParQRegister from '../pages/ParQ/ParQRegister/ParQRegister';
import Training from '../pages/Training/Training';
import Commercial from '../pages/Commercial/Commercial';
import OnboardingPage from '../pages/Onboarding/Onboarding';
import MarketingProjectLeads from '../pages/MarketingProjects/MarketingProjectLeads';
import Administration from '../pages/Administration/Administration';
import UserRegister from '../pages/Administration/UserRegister/UserRegister';
import Exercises from '../pages/Exercises/Exercises';
import ExerciseRegister from '../pages/Exercises/ExerciseRegister/ExerciseRegister';
import TrainingRegister from '../pages/Training/TrainingRegister/TrainingRegister';
import Fup from '../pages/Fup/Fup';
import FupPublic from '../pages/Fup/FupPublic';
import FupThankYou from '../pages/Fup/FupThankYou';
import AccountSettings from '../pages/Account/AccountSettings';
import Help from '../pages/Help/Help';
import HelpDocumentPage from '../pages/Help/HelpDocumentPage';
import PhotoGuidelinesPage from '../pages/PhotoGuidelines';
import EquipmentPhotosPublicPage from '../pages/EquipmentPhotos/EquipmentPhotosPublicPage';
import CheckoutPage from '../pages/Checkout';
import CheckoutSuccessPage from '../pages/Checkout/CheckoutSuccessPage';
import CheckoutAwaitingPaymentPage from '../pages/Checkout/CheckoutAwaitingPaymentPage';
import LeadQualificationPage from '../pages/LeadQualification';
import {
  MealDetailPage,
  MealListPage,
  MealTrackingLayout,
  SupplementListPage,
} from '../pages/MealTracking';
import { MedicalExamsPage } from '../pages/MedicalExams';
import { BodyMeasurementsPage } from '../pages/BodyMeasurements';
import {
  getCheckoutRoutePaths,
  getCurrentHostApp,
  getOnboardingRoutePaths,
  getQueroComecarCanonicalUrl,
  isApexOrWwwHostname,
  type HostApp,
} from '../config/hostApps';
import {
  ADMIN_ONLY_ROLES,
  ADMINISTRATION_ROLES,
  COACH_ROLES,
  COMMERCIAL_DASHBOARD_ROLES,
  COMMERCIAL_ROLES,
  DASHBOARD_ROLES,
  ONBOARDING_ROLES,
  PERFORMANCE_SUPPORT_DASHBOARD_ROLES,
  RECENT_BODY_PHOTOS_DASHBOARD_ROLES,
  SENSITIVE_STUDENTS_DASHBOARD_ROLES,
  ORCHESTRATOR_ROLES,
  STUDENT_VIEW_ROLES,
} from '../utils/permissions';
import {
  CINTA_NATURAL_ONBOARDING_PROJECT,
  ONBOARDING_PROJECT_QUERY_PARAM,
} from '../utils/studentPublicLinks';

const onboardingPaths = getOnboardingRoutePaths();

const RedirectPreserveSearch: React.FC<{ to: string }> = ({ to }) => {
  const [params] = useSearchParams();
  const search = params.toString();
  return <Navigate to={search ? `${to}?${search}` : to} replace />;
};

const RedirectCintaNaturalOnboarding: React.FC = () => {
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  next.set(ONBOARDING_PROJECT_QUERY_PARAM, CINTA_NATURAL_ONBOARDING_PROJECT);
  return <Navigate to={`${onboardingPaths.root}?${next.toString()}`} replace />;
};

const RedirectTreinamentosToTreinos: React.FC = () => {
  const location = useLocation();
  const nextPath = location.pathname.replace(/^\/treinamentos(?=\/|$)/, '/treinos');
  return <Navigate to={`${nextPath}${location.search}${location.hash}`} replace />;
};

/** Rotas do fluxo onboarding (landpage + anamnese + parq + fotos). */
const onboardingFlowRouteElements = (
  <>
    <Route path={onboardingPaths.root} element={<Landpage />} />
    <Route path="/cinta-natural" element={<RedirectCintaNaturalOnboarding />} />
    <Route path={onboardingPaths.anamnese} element={<AnamnesisRegister />} />
    <Route path={onboardingPaths.parq} element={<ParQRegister />} />
    <Route path={onboardingPaths.parqAlias} element={<ParQRegister />} />
    <Route path={onboardingPaths.enviarFotos} element={<PhotoGuidelinesPage />} />
    <Route path={onboardingPaths.equipamentos} element={<EquipmentPhotosPublicPage />} />
    {/* Compat: paths antigos → /onboarding/* (preserva ?s=) */}
    <Route path="/anamnese" element={<RedirectPreserveSearch to={onboardingPaths.anamnese} />} />
    <Route path="/parq" element={<RedirectPreserveSearch to={onboardingPaths.parq} />} />
    <Route path="/par-q" element={<RedirectPreserveSearch to={onboardingPaths.parq} />} />
    <Route path="/enviar-fotos" element={<RedirectPreserveSearch to={onboardingPaths.enviarFotos} />} />
    <Route path="/equipamentos" element={<RedirectPreserveSearch to={onboardingPaths.equipamentos} />} />
    <Route path="/enviar-equipamentos" element={<RedirectPreserveSearch to={onboardingPaths.equipamentos} />} />
    <Route path="/onboarding/enviar-equipamentos" element={<RedirectPreserveSearch to={onboardingPaths.equipamentos} />} />
    <Route path="/enviar-foto/:studentGuid" element={<RedirectPreserveSearch to={onboardingPaths.root} />} />
    <Route path="/enviar-foto" element={<RedirectPreserveSearch to={onboardingPaths.root} />} />
    <Route path="/landpage" element={<RedirectPreserveSearch to={onboardingPaths.root} />} />
  </>
);

const PortalRoutes: React.FC = () => (
  <Routes>
    <Route path="/login" element={<Login />} />
    <Route path="/cadastro" element={<Navigate to="/login" replace />} />
    <Route path="/aplicar" element={<LeadForm />} />
    <Route path={onboardingPaths.root} element={<Landpage />} />
    <Route path="/cinta-natural" element={<RedirectCintaNaturalOnboarding />} />
    <Route path="/diario-alimentar" element={<MealTrackingLayout />}>
      <Route index element={<MealListPage />} />
      <Route path="nova" element={<MealListPage />} />
      <Route path=":mealId" element={<MealDetailPage />} />
      <Route path=":mealId/editar" element={<MealDetailPage />} />
    </Route>
    <Route path="/enviar-suplementos" element={<MealTrackingLayout />}>
      <Route index element={<SupplementListPage />} />
    </Route>
    <Route path="/enviar-exames" element={<MedicalExamsPage />} />
    <Route path="/enviar-medidas" element={<BodyMeasurementsPage />} />

    <Route path="/" element={<PrivateRoute allowedRoles={DASHBOARD_ROLES}><Dashboard /></PrivateRoute>} />
    <Route path="/a-vencer" element={<PrivateRoute allowedRoles={DASHBOARD_ROLES}><Dashboard /></PrivateRoute>} />
    <Route path="/metas" element={<PrivateRoute allowedRoles={COMMERCIAL_DASHBOARD_ROLES}><Dashboard /></PrivateRoute>} />
    <Route path="/recebimentos" element={<PrivateRoute allowedRoles={DASHBOARD_ROLES}><Dashboard /></PrivateRoute>} />
    <Route path="/suporte-performance" element={<PrivateRoute allowedRoles={PERFORMANCE_SUPPORT_DASHBOARD_ROLES}><Dashboard /></PrivateRoute>} />
    <Route path="/fotos-recentes" element={<PrivateRoute allowedRoles={RECENT_BODY_PHOTOS_DASHBOARD_ROLES}><Dashboard /></PrivateRoute>} />
    <Route path="/alunas-sensiveis" element={<PrivateRoute allowedRoles={SENSITIVE_STUDENTS_DASHBOARD_ROLES}><Dashboard /></PrivateRoute>} />
    <Route path="/dashboard/comercial" element={<Navigate to="/" replace />} />
    <Route path="/dashboard/projetos" element={<Navigate to="/" replace />} />
    <Route path="/dashboard-comercial" element={<Navigate to="/" replace />} />
    <Route path="/alunas" element={<PrivateRoute allowedRoles={STUDENT_VIEW_ROLES}><StudentList /></PrivateRoute>} />
    <Route path="/alunas/nova" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><StudentRegister /></PrivateRoute>} />
    <Route path="/alunas/:id/editar" element={<PrivateRoute allowedRoles={STUDENT_VIEW_ROLES}><StudentRegister /></PrivateRoute>} />
    <Route path="/alunas/:id/treino" element={<PrivateRoute allowedRoles={COACH_ROLES}><StudentTraining /></PrivateRoute>} />
    <Route path="/alunas/:studentId/treino/:studentTrainingId" element={<PrivateRoute allowedRoles={COACH_ROLES}><TrainingRegister /></PrivateRoute>} />
    <Route path="/pagamentos" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><Payments /></PrivateRoute>} />
    <Route path="/sys" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><SysHome /></PrivateRoute>} />
    <Route path="/sys/logs" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><Logs /></PrivateRoute>} />
    <Route path="/sys/payment-errors" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><PaymentErrors /></PrivateRoute>} />
    <Route path="/logs" element={<RedirectPreserveSearch to="/sys/logs" />} />
    <Route path="/fix-subscriptions" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><FixSubscriptions /></PrivateRoute>} />
    <Route path="/orquestrador" element={<PrivateRoute allowedRoles={ORCHESTRATOR_ROLES}><Orchestrator /></PrivateRoute>} />
    <Route path="/orquestrador/fup" element={<PrivateRoute allowedRoles={ORCHESTRATOR_ROLES}><OrchestratorFup /></PrivateRoute>} />
    <Route path="/performance" element={<PrivateRoute allowedRoles={COACH_ROLES}><Performance /></PrivateRoute>} />
    <Route path="/performance/alunas/:studentId/historico" element={<PrivateRoute allowedRoles={COACH_ROLES}><StudentHistoryPage /></PrivateRoute>} />
    <Route path="/gestao-entregas-treinos" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><WorkoutDeliveryManagementPage /></PrivateRoute>} />
    <Route path="/treinos" element={<PrivateRoute allowedRoles={COACH_ROLES}><Training /></PrivateRoute>} />
    <Route path="/treinos/novo" element={<PrivateRoute allowedRoles={COACH_ROLES}><TrainingRegister /></PrivateRoute>} />
    <Route path="/treinos/:trainingId/editar" element={<PrivateRoute allowedRoles={COACH_ROLES}><TrainingRegister /></PrivateRoute>} />
    <Route path="/treinamentos" element={<RedirectTreinamentosToTreinos />} />
    <Route path="/treinamentos/*" element={<RedirectTreinamentosToTreinos />} />
    <Route path="/comercial" element={<PrivateRoute allowedRoles={COMMERCIAL_ROLES}><Commercial /></PrivateRoute>} />
    <Route path="/comercial/declinadas" element={<PrivateRoute allowedRoles={COMMERCIAL_ROLES}><Commercial /></PrivateRoute>} />
    <Route path="/acompanhamento-onboarding" element={<PrivateRoute allowedRoles={ONBOARDING_ROLES}><OnboardingPage /></PrivateRoute>} />
    <Route path="/projetos/:projectCode" element={<PrivateRoute allowedRoles={COMMERCIAL_ROLES}><MarketingProjectLeads /></PrivateRoute>} />
    <Route path="/exercicios" element={<PrivateRoute allowedRoles={COACH_ROLES}><Exercises /></PrivateRoute>} />
    <Route path="/exercicios/novo" element={<PrivateRoute allowedRoles={COACH_ROLES}><ExerciseRegister /></PrivateRoute>} />
    <Route path="/exercicios/:exerciseId/editar" element={<PrivateRoute allowedRoles={COACH_ROLES}><ExerciseRegister /></PrivateRoute>} />
    <Route path="/administracao" element={<PrivateRoute allowedRoles={ADMINISTRATION_ROLES}><Administration /></PrivateRoute>} />
    <Route path="/administracao/usuarios/cadastro" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><UserRegister /></PrivateRoute>} />
    <Route path="/administracao/usuarios/:userId/editar" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><UserRegister /></PrivateRoute>} />
    <Route path="/fup" element={<PrivateRoute><Fup /></PrivateRoute>} />
    <Route path="/conta" element={<PrivateRoute><AccountSettings /></PrivateRoute>} />
    <Route path="/ajuda" element={<PrivateRoute><Help /></PrivateRoute>} />
    <Route path="/ajuda/:documentId" element={<PrivateRoute><HelpDocumentPage /></PrivateRoute>} />

    <Route path="*" element={<NotFound />} />
  </Routes>
);

/** Modo desenvolvimento: todos os paths no mesmo host. */
const LegacyRoutes: React.FC = () => {
  const checkoutPaths = getCheckoutRoutePaths('legacy');

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/cadastro" element={<Navigate to="/login" replace />} />
      <Route path="/aplicar" element={<LeadForm />} />
      {onboardingFlowRouteElements}
      <Route path={checkoutPaths.root} element={<CheckoutPage />} />
      <Route path={checkoutPaths.awaitingPayment} element={<CheckoutAwaitingPaymentPage />} />
      <Route path={checkoutPaths.success} element={<CheckoutSuccessPage />} />
      <Route path="/quero-comecar" element={<LeadQualificationPage />} />
      <Route path="/diario-alimentar" element={<MealTrackingLayout />}>
        <Route index element={<MealListPage />} />
        <Route path="nova" element={<MealListPage />} />
        <Route path=":mealId" element={<MealDetailPage />} />
        <Route path=":mealId/editar" element={<MealDetailPage />} />
      </Route>
      <Route path="/enviar-suplementos" element={<MealTrackingLayout />}>
        <Route index element={<SupplementListPage />} />
      </Route>
      <Route path="/enviar-exames" element={<MedicalExamsPage />} />
      <Route path="/enviar-medidas" element={<BodyMeasurementsPage />} />

      <Route path="/" element={<PrivateRoute allowedRoles={DASHBOARD_ROLES}><Dashboard /></PrivateRoute>} />
      <Route path="/a-vencer" element={<PrivateRoute allowedRoles={DASHBOARD_ROLES}><Dashboard /></PrivateRoute>} />
      <Route path="/metas" element={<PrivateRoute allowedRoles={COMMERCIAL_DASHBOARD_ROLES}><Dashboard /></PrivateRoute>} />
      <Route path="/recebimentos" element={<PrivateRoute allowedRoles={DASHBOARD_ROLES}><Dashboard /></PrivateRoute>} />
    <Route path="/suporte-performance" element={<PrivateRoute allowedRoles={PERFORMANCE_SUPPORT_DASHBOARD_ROLES}><Dashboard /></PrivateRoute>} />
      <Route path="/fotos-recentes" element={<PrivateRoute allowedRoles={RECENT_BODY_PHOTOS_DASHBOARD_ROLES}><Dashboard /></PrivateRoute>} />
      <Route path="/alunas-sensiveis" element={<PrivateRoute allowedRoles={SENSITIVE_STUDENTS_DASHBOARD_ROLES}><Dashboard /></PrivateRoute>} />
      <Route path="/dashboard/comercial" element={<Navigate to="/" replace />} />
      <Route path="/dashboard/projetos" element={<Navigate to="/" replace />} />
      <Route path="/dashboard-comercial" element={<Navigate to="/" replace />} />
      <Route path="/alunas" element={<PrivateRoute allowedRoles={STUDENT_VIEW_ROLES}><StudentList /></PrivateRoute>} />
      <Route path="/alunas/nova" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><StudentRegister /></PrivateRoute>} />
      <Route path="/alunas/:id/editar" element={<PrivateRoute allowedRoles={STUDENT_VIEW_ROLES}><StudentRegister /></PrivateRoute>} />
      <Route path="/alunas/:id/treino" element={<PrivateRoute allowedRoles={COACH_ROLES}><StudentTraining /></PrivateRoute>} />
      <Route path="/alunas/:studentId/treino/:studentTrainingId" element={<PrivateRoute allowedRoles={COACH_ROLES}><TrainingRegister /></PrivateRoute>} />
      <Route path="/pagamentos" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><Payments /></PrivateRoute>} />
      <Route path="/sys" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><SysHome /></PrivateRoute>} />
      <Route path="/sys/logs" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><Logs /></PrivateRoute>} />
      <Route path="/sys/payment-errors" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><PaymentErrors /></PrivateRoute>} />
      <Route path="/logs" element={<RedirectPreserveSearch to="/sys/logs" />} />
      <Route path="/fix-subscriptions" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><FixSubscriptions /></PrivateRoute>} />
      <Route path="/orquestrador" element={<PrivateRoute allowedRoles={ORCHESTRATOR_ROLES}><Orchestrator /></PrivateRoute>} />
      <Route path="/orquestrador/fup" element={<PrivateRoute allowedRoles={ORCHESTRATOR_ROLES}><OrchestratorFup /></PrivateRoute>} />
      <Route path="/performance" element={<PrivateRoute allowedRoles={COACH_ROLES}><Performance /></PrivateRoute>} />
      <Route path="/performance/alunas/:studentId/historico" element={<PrivateRoute allowedRoles={COACH_ROLES}><StudentHistoryPage /></PrivateRoute>} />
      <Route path="/gestao-entregas-treinos" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><WorkoutDeliveryManagementPage /></PrivateRoute>} />
      <Route path="/treinos" element={<PrivateRoute allowedRoles={COACH_ROLES}><Training /></PrivateRoute>} />
      <Route path="/treinos/novo" element={<PrivateRoute allowedRoles={COACH_ROLES}><TrainingRegister /></PrivateRoute>} />
      <Route path="/treinos/:trainingId/editar" element={<PrivateRoute allowedRoles={COACH_ROLES}><TrainingRegister /></PrivateRoute>} />
      <Route path="/treinamentos" element={<RedirectTreinamentosToTreinos />} />
      <Route path="/treinamentos/*" element={<RedirectTreinamentosToTreinos />} />
      <Route path="/comercial" element={<PrivateRoute allowedRoles={COMMERCIAL_ROLES}><Commercial /></PrivateRoute>} />
      <Route path="/comercial/declinadas" element={<PrivateRoute allowedRoles={COMMERCIAL_ROLES}><Commercial /></PrivateRoute>} />
      <Route path="/acompanhamento-onboarding" element={<PrivateRoute allowedRoles={ONBOARDING_ROLES}><OnboardingPage /></PrivateRoute>} />
      <Route path="/projetos/:projectCode" element={<PrivateRoute allowedRoles={COMMERCIAL_ROLES}><MarketingProjectLeads /></PrivateRoute>} />
      <Route path="/exercicios" element={<PrivateRoute allowedRoles={COACH_ROLES}><Exercises /></PrivateRoute>} />
      <Route path="/exercicios/novo" element={<PrivateRoute allowedRoles={COACH_ROLES}><ExerciseRegister /></PrivateRoute>} />
      <Route path="/exercicios/:exerciseId/editar" element={<PrivateRoute allowedRoles={COACH_ROLES}><ExerciseRegister /></PrivateRoute>} />
      <Route path="/administracao" element={<PrivateRoute allowedRoles={ADMINISTRATION_ROLES}><Administration /></PrivateRoute>} />
      <Route path="/administracao/usuarios/cadastro" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><UserRegister /></PrivateRoute>} />
      <Route path="/administracao/usuarios/:userId/editar" element={<PrivateRoute allowedRoles={ADMIN_ONLY_ROLES}><UserRegister /></PrivateRoute>} />
      <Route path="/fup/:token/:studentSlug/obrigado" element={<FupThankYou />} />
      <Route path="/fup/:token/obrigado" element={<FupThankYou />} />
      <Route path="/fup/:token/:studentSlug" element={<FupPublic />} />
      <Route path="/fup/:token" element={<FupPublic />} />
      <Route path="/fup" element={<PrivateRoute><Fup /></PrivateRoute>} />
      <Route path="/conta" element={<PrivateRoute><AccountSettings /></PrivateRoute>} />
      <Route path="/ajuda" element={<PrivateRoute><Help /></PrivateRoute>} />
      <Route path="/ajuda/:documentId" element={<PrivateRoute><HelpDocumentPage /></PrivateRoute>} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

/**
 * Apex/www → Quero Começar (fallback se o Edge Middleware / vercel.json
 * ainda não redirecionaram). Demais hosts desconhecidos: fail closed.
 */
const DisabledHostRoutes: React.FC = () => {
  React.useEffect(() => {
    if (isApexOrWwwHostname()) {
      window.location.replace(getQueroComecarCanonicalUrl());
    }
  }, []);

  return (
    <Routes>
      <Route path="*" element={null} />
    </Routes>
  );
};

const FeatureHostRoutes: React.FC<{ app: HostApp }> = ({ app }) => {
  if (app === 'onboarding') {
    return (
      <Routes>
        <Route path="/" element={<RedirectPreserveSearch to={onboardingPaths.root} />} />
        {/* Paths canônicos: /anamnese, /parq, /enviar-fotos, /equipamentos no host onboarding */}
        <Route path="/anamnese" element={<AnamnesisRegister />} />
        <Route path="/parq" element={<ParQRegister />} />
        <Route path="/par-q" element={<ParQRegister />} />
        <Route path="/enviar-fotos" element={<PhotoGuidelinesPage />} />
        <Route path="/equipamentos" element={<EquipmentPhotosPublicPage />} />
        <Route path="/enviar-equipamentos" element={<RedirectPreserveSearch to="/equipamentos" />} />
        {onboardingFlowRouteElements}
        <Route path="*" element={<RedirectPreserveSearch to={onboardingPaths.root} />} />
      </Routes>
    );
  }

  if (app === 'anamnese' || app === 'parq' || app === 'enviar-fotos' || app === 'equipamentos') {
    const rootElement = app === 'anamnese'
      ? <AnamnesisRegister />
      : app === 'parq'
        ? <ParQRegister />
        : app === 'equipamentos'
          ? <EquipmentPhotosPublicPage />
          : <PhotoGuidelinesPage />;

    return (
      <Routes>
        {/* Formulário direto na raiz do subdomínio (link compartilhado). */}
        <Route path="/" element={rootElement} />
        {/* Compat: paths internos/antigos continuam funcionando neste host. */}
        {onboardingFlowRouteElements}
        <Route path="*" element={<RedirectPreserveSearch to="/" />} />
      </Routes>
    );
  }

  if (app === 'checkout') {
    const checkoutPaths = getCheckoutRoutePaths('checkout');
    return (
      <Routes>
        <Route path={checkoutPaths.root} element={<CheckoutPage />} />
        <Route path={checkoutPaths.success} element={<CheckoutSuccessPage />} />
        <Route path={checkoutPaths.awaitingPayment} element={<CheckoutAwaitingPaymentPage />} />
        {/* Compatibilidade com paths legados no mesmo subdomínio */}
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        <Route path="/checkout/awaiting-payment" element={<CheckoutAwaitingPaymentPage />} />
        <Route path="*" element={<RedirectPreserveSearch to="/" />} />
      </Routes>
    );
  }

  if (app === 'quero-comecar') {
    return (
      <Routes>
        <Route path="/" element={<LeadQualificationPage />} />
        <Route path="/quero-comecar" element={<LeadQualificationPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  if (app === 'fup') {
    return (
      <Routes>
        <Route path="/:token/:studentSlug/obrigado" element={<FupThankYou />} />
        <Route path="/:token/obrigado" element={<FupThankYou />} />
        <Route path="/:token/:studentSlug" element={<FupPublic />} />
        <Route path="/:token" element={<FupPublic />} />
        <Route path="/fup/:token/:studentSlug/obrigado" element={<FupThankYou />} />
        <Route path="/fup/:token/obrigado" element={<FupThankYou />} />
        <Route path="/fup/:token/:studentSlug" element={<FupPublic />} />
        <Route path="/fup/:token" element={<FupPublic />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    );
  }

  return <DisabledHostRoutes />;
};

const AppRoutes: React.FC = () => {
  const hostApp = getCurrentHostApp();

  if (hostApp === 'legacy') {
    return <LegacyRoutes />;
  }

  if (hostApp === 'disabled') {
    return <DisabledHostRoutes />;
  }

  if (hostApp === 'portal') {
    return <PortalRoutes />;
  }

  return <FeatureHostRoutes app={hostApp} />;
};

export default AppRoutes;
